import React, { Fragment, useState, useEffect, useCallback } from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Box,
  Divider,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  AppBar,
  Toolbar, FormControl, Select, MenuItem, Tabs, Tab, TextField, Radio, RadioGroup, FormControlLabel,
  Chip,
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { HttpRequest } from "../../../utils/httpRequests";
import NavBar from "../../../components/NavBar/NavBar";
import CustomizedSnackbar from "../../../components/CustomizedSnackbar/CustomizedSnackbar";
import LoginMenu from "../../../components/LoginMenu/LoginMenu";
import { Skeleton } from "@material-ui/lab";
import PublishIcon from "@material-ui/icons/Publish";
import { Tooltip } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import { InputAdornment } from "@material-ui/core";
import LayoutMethods from "../../../components/Layouts/layoutMethods";

const useStyles = makeStyles(() => ({
  root: {
    padding: 10,
    margin: "70px 20px",
  },
  categoryHeading: {
    fontWeight: 700,
    marginBottom: 20,
    marginTop: 20,
  },
  productCard: {
    marginBottom: 20,
    padding: 15,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
      transform: "translateY(-2px)",
    },
  },
  productLabel: {
    fontWeight: 600,
    marginBottom: 15,
    color: "#333",
  },
  assetCard: {
    cursor: "pointer",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  assetImage: {
    // height: 200,
    // objectFit: "cover",
    // width: "100%",
    // height handled by aspect ratio wrapper in LazyImage
  },
  assetFileName: {
    fontSize: 12,
    color: "#666",
    wordBreak: "break-word",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "60vh",
  },
  loadMoreButton: {
    marginTop: 20,
    marginBottom: 20,
  },
  imageSkeleton: {
    // height: 200,
    // height handled by aspect ratio wrapper in LazyImage
  },
  dialogPaper: {
    minHeight: "90vh",
    maxHeight: "90vh",
  },
  dialogContent: {
    padding: 20,
  },
  appBar: {
    position: "relative",
  },
  dialogTitle: {
    flex: 1,
  },
  viewAssetsButton: {
    marginTop: 10,
  },
  assetCount: {
    fontSize: 14,
    color: "#666",
    marginTop: 5,
  },
}));


const getAspectRatio = (size, fileName, selectedCategoryId) => {
  const name = fileName?.toLowerCase() || "";

  if (selectedCategoryId === 2) {
    if (size === "Landscape") {
      if (name.startsWith("1_")) return 3796 / 3000; // Front Cover
      if (name.startsWith("24_")) return 4010 / 3000; // Back Cover
      return 3508 / 2400;  // Inner Pages
    }

    if (size === "Square") {
      if (name.startsWith("1_")) return 2737 / 3000; // Front Cover
      if (name.startsWith("24_")) return 2961 / 3000; // Back Cover
      return 2400 / 2400;  // Inner Pages
    }
  } else if (selectedCategoryId === 9) {
    if (size === "Regular Mug") return 2550 / 1050;
    if (size === "Big Mug") return 2550 / 1125;
    return 2550 / 1050; // fallback to Regular
  }

  return 1; // fallback 1:1

};


const getExpectedAspectRatio = (size, fileName, selectedCategoryId, totalPages) => {
  // ic and fl share the same aspect ratio — ic is just a scaled-down version.
  const name = fileName?.toLowerCase() || "";

  if (selectedCategoryId === 2) {
    const backCoverPageNo = totalPages ? totalPages + 4 : 24;
    if (size === "Landscape") {
      if (name.startsWith("1_") || name.startsWith("page1_")) return 3796 / 3000; // 1.2653 Front Cover
      if (name.startsWith(`${backCoverPageNo}_`) || name.startsWith(`page${backCoverPageNo}_`)) return 4010 / 3000; // 1.3367 Back Cover
      return 3508 / 2400; // 1.4617 Inner Pages
    }
    if (size === "Square") {
      if (name.startsWith("1_") || name.startsWith("page1_")) return 2737 / 3000; // 0.9123 Front Cover
      if (name.startsWith(`${backCoverPageNo}_`) || name.startsWith(`page${backCoverPageNo}_`)) return 2961 / 3000; // 0.9870 Back Cover
      return 2400 / 2400; // 1.0000 Inner Pages
    }
  } else if (selectedCategoryId === 9) {
    if (size === "Regular Mug") return 2550 / 1050; // 2.4286
    if (size === "Big Mug") return 2550 / 1125; // 2.2667
    return 2550 / 1050; // fallback
  }

  return null; // no rule — skip validation
};

const ASPECT_RATIO_TOLERANCE = 0.01; // ±0.01 ratio tolerance (~0.8%)

const LazyImage = ({ src, alt, className, aspectRatio, selectedCategoryId, fixedHeight }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);
  const classes = useStyles();

  useEffect(() => {
    if (!src) return;
    let objectUrl = null;

    const fetchImage = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(src, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch (e) {
        console.error("Image fetch error:", e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImage();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [src]);

  const paddingTop = aspectRatio ? `${(1 / aspectRatio) * 100}%` : "100%";
  const containerStyle = fixedHeight
    ? { position: "relative", width: "100%", height: fixedHeight }
    : { position: "relative", width: "100%", paddingTop };

  return (
    <div style={containerStyle}>
      {isLoading && (
        <Skeleton
          variant="rect"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
        />
      )}
      {blobUrl && (
        <img
          src={blobUrl}
          alt={alt}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            backgroundColor: "#a9a9a96b",
            display: "block",

          }}
        />
      )}
    </div>
  );
};

const getGridSize = (selectedCategoryId) => {
  if (selectedCategoryId === 9) {
    return { xs: 8, sm: 8, md: 8, lg: 8 }; // full width for mug
  }
  return { xs: 6, sm: 4, md: 3, lg: 2 }; // default
};

// Extract sizeAlias from filename: "1_ic_tm_pb_ms_..." → index 4 → "ms"
const getSizeAliasFromFileName = (fileName) => {
  if (!fileName) return null;
  const parts = fileName.split("_");
  return parts.length >= 5 ? parts[4] : null;
};

// Design export dimensions — must match layout method calibration
const getExportDims = (categoryId, sizeId, isFrontCover, isBackCover) => {
  const id = parseInt(categoryId);
  let w = 3508, h = 2400;
  if (id === 2) {
    if (sizeId == 2) {
      if (isFrontCover)      { w = 3796; h = 3000; }
      else if (isBackCover)  { w = 4010; h = 3000; }
      else                   { w = 3508; h = 2400; }
    } else if (sizeId == 4) {
      if (isFrontCover)      { w = 2737; h = 3000; }
      else if (isBackCover)  { w = 2961; h = 3000; }
      else                   { w = 2400; h = 2400; }
    }
  } else if (id === 6) {
    w = 3600; h = sizeId == 15 ? 3600 : 5400;
  } else if (id === 9) {
    w = 2550; h = sizeId == 23 ? 1125 : 1050;
  }
  return { w, h };
};

// LayoutPreview — same as approval page
const LayoutPreview = ({ exportWidth, exportHeight, collageInfoList }) => {
  if (!exportWidth || !exportHeight) return null;
  const ratio = exportWidth / exportHeight;
  const paddingTop = (1 / ratio) * 100;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
        <span style={{ fontSize: 9, color: "#7986cb", fontWeight: 600, letterSpacing: 0.3 }}>LAYOUT PREVIEW</span>
        <span style={{ fontSize: 9, color: "#9fa8da", fontWeight: 500 }}>{exportWidth} × {exportHeight} px</span>
      </div>
      <div style={{ position: "relative", width: "100%", paddingTop: `${paddingTop}%`, backgroundColor: "#e8eaf6", border: "1.5px solid #9fa8da", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)", backgroundSize: "10% 10%" }} />
        {collageInfoList && collageInfoList.map((info, i) => {
          const slotWpx = Math.round(info.getWidth() * exportWidth);
          const slotHpx = Math.round(info.getHeight() * exportHeight);
          const slotLpx = Math.round(info.getLeft() * exportWidth);
          const slotTpx = Math.round(info.getTop() * exportHeight);
          return (
            <div key={i} style={{ position: "absolute", left: `${info.getLeft()*100}%`, top: `${info.getTop()*100}%`, width: `${info.getWidth()*100}%`, height: `${info.getHeight()*100}%`, backgroundColor: "rgba(211,47,47,0.15)", border: "2px solid rgba(211,47,47,0.85)", borderRadius: 2, boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, padding: 2 }}>
              <span style={{ fontSize: 8, color: "rgba(183,28,28,0.95)", fontWeight: 700, userSelect: "none" }}>SLOT {i+1}</span>
              <span style={{ fontSize: 7, color: "rgba(183,28,28,0.8)", fontWeight: 500, userSelect: "none" }}>{slotWpx} × {slotHpx}</span>
              <span style={{ fontSize: 7, color: "rgba(120,20,20,0.6)", fontWeight: 400, userSelect: "none" }}>@ {slotLpx}, {slotTpx}</span>
            </div>
          );
        })}
        {(!collageInfoList || collageInfoList.length === 0) && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 10, color: "#9fa8da", fontWeight: 500 }}>No layout data</span>
          </div>
        )}
      </div>
      {collageInfoList && collageInfoList.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {collageInfoList.map((info, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span style={{ fontSize: 8, color: "#d32f2f", fontWeight: 600 }}>Slot {i+1}</span>
              <span style={{ fontSize: 8, color: "#555" }}>{Math.round(info.getWidth()*exportWidth)} × {Math.round(info.getHeight()*exportHeight)} px</span>
              <span style={{ fontSize: 8, color: "#888" }}>x:{Math.round(info.getLeft()*exportWidth)} y:{Math.round(info.getTop()*exportHeight)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// LayoutPickerDialog — functional version
const LayoutPickerDialog = ({ open, onClose, layoutsList, onSelect, selectedLayoutNo, getCollageOverlay }) => {
  const [filterPictures, setFilterPictures] = useState(null);
  const pictureOptions = [...new Set((layoutsList || []).map(l => l.totalPictures).filter(v => v != null))].sort((a,b) => a-b);
  const filtered = (layoutsList || []).filter(l => filterPictures == null ? true : l.totalPictures === filterPictures);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: 12, maxHeight: "90vh" } }}>
      <DialogTitle style={{ paddingBottom: 8 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" style={{ fontWeight: 700, color: "#333" }}>Select Layout</Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
        <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginTop: 10 }}>
          <Chip label="All" size="small" onClick={() => setFilterPictures(null)}
            style={{ backgroundColor: filterPictures == null ? "#1976d2" : "#e0e0e0", color: filterPictures == null ? "#fff" : "#333", fontWeight: 600, cursor: "pointer" }} />
          {pictureOptions.map(n => (
            <Chip key={n} label={`${n} Picture${n !== 1 ? "s" : ""}`} size="small" onClick={() => setFilterPictures(n)}
              style={{ backgroundColor: filterPictures === n ? "#1976d2" : "#e0e0e0", color: filterPictures === n ? "#fff" : "#333", fontWeight: 600, cursor: "pointer" }} />
          ))}
        </Box>
      </DialogTitle>
      <DialogContent dividers style={{ padding: 16 }}>
        {filtered.length === 0 && <Typography variant="body2" style={{ color: "#888", textAlign: "center", marginTop: 32 }}>No layouts available.</Typography>}
        <Grid container spacing={2}>
          {filtered.map(layout => {
            const overlay = getCollageOverlay(layout.layoutNo);
            const isSelected = layout.layoutNo === selectedLayoutNo;
            return (
              <Grid item xs={12} sm={6} md={4} key={layout.layoutId}>
                <Card elevation={isSelected ? 6 : 2} onClick={() => { onSelect(layout); onClose(); }}
                  style={{ borderRadius: 8, border: isSelected ? "2px solid #1976d2" : "2px solid transparent", cursor: "pointer", overflow: "hidden" }}>
                  <Box style={{ padding: "10px 10px 0 10px", backgroundColor: isSelected ? "#e3f2fd" : "#fafafa" }}>
                    <LayoutPreview exportWidth={overlay?.exportWidth} exportHeight={overlay?.exportHeight} collageInfoList={overlay?.collageInfoList} />
                  </Box>
                  <CardContent style={{ padding: "8px 10px 10px 10px", backgroundColor: isSelected ? "#e3f2fd" : "#fff" }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" style={{ fontWeight: 700, color: isSelected ? "#1976d2" : "#333", fontSize: 12 }}>
                        {layout.layoutNo}{layout.label ? ` — ${layout.label}` : ""}
                      </Typography>
                      {isSelected && <Chip label="Selected" size="small" style={{ backgroundColor: "#1976d2", color: "#fff", fontSize: 9, height: 18 }} />}
                    </Box>
                    {layout.totalPictures != null && (
                      <Typography variant="caption" style={{ color: "#888", display: "block", marginTop: 2 }}>{layout.totalPictures} picture{layout.totalPictures !== 1 ? "s" : ""}</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

const AssetsModal = ({ open, onClose, product, getImageUrl, isAssetsLoading, onAssetUpload, uploadingAssetId, classes, selectedCategoryId, onDescriptionSave, isSavingDescription, onValidationError, onSuccess, onProductFieldChange, layoutsList, sizesList, onResubmitApproval, isResubmitting }) => {
  const [activeTab, setActiveTab] = useState(0);

  // ── Status / Trending local state ──
  const [isActive, setIsActive] = useState(1);
  const [isTrending, setIsTrending] = useState(0);
  // ── Edit states ──
  const [editingThemeLabel, setEditingThemeLabel] = useState(false);
  const [editingProductLabel, setEditingProductLabel] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);

  const [themeLabelValue, setThemeLabelValue] = useState("");
  const [productLabelValue, setProductLabelValue] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");

  // ── Preview/Thumbnail states ──
  const [previewImage, setPreviewImage] = useState(null);
  const [thumbnailImage, setThumbnailImage] = useState(null);

  // ── Layout picker state ──
  const [layoutPickerPage, setLayoutPickerPage] = useState(null); // pageIndex of open picker, or null
  const [pageLayouts, setPageLayouts] = useState({}); // { [pageIndex]: { layoutNo, layoutId } }
  const [savingLayoutPage, setSavingLayoutPage] = useState(null); // pageIndex currently saving layout

  useEffect(() => {
    setActiveTab(0);
    setEditingThemeLabel(false);
    setEditingProductLabel(false);
    setEditingDescription(false);
    setThemeLabelValue(product?.userLabel || "");
    setProductLabelValue(product?.label || "");
    setDescriptionValue(product?.description || "");
    setIsActive(product?.isActive != null ? product.isActive : "1");
    setIsTrending(product?.isTrending != null ? product.isTrending : "0");
    setPreviewImage(null);
    setThumbnailImage(null);
    setPageLayouts({});
    setLayoutPickerPage(null);
  }, [open, product]);

  if (!product) return null;

  const isRejected = product.isApproved == 2;
  const assetGroups = product.assetGroups || [];

  // Parse assets: assetTypeId==1 → appAssets (ic), assetTypeId==2 → printAssets (fl)
  const appAssetGroup = assetGroups.find(g => g.assetTypeId == 1);
  const printAssetGroup = assetGroups.find(g => g.assetTypeId == 2);
  const appAssets = appAssetGroup ? appAssetGroup.assets || [] : [];
  const printAssets = printAssetGroup ? printAssetGroup.assets || [] : [];

  // Determine maxPageNo from filenames (first part before "_")
  const allAssets = [...appAssets, ...printAssets];
  let maxPageNo = 0;
  allAssets.forEach(a => {
    const pageNo = parseInt((a.fileName || "").split("_")[0]);
    if (!isNaN(pageNo) && pageNo > maxPageNo) maxPageNo = pageNo;
  });
  const pageCount = maxPageNo > 0 ? maxPageNo : Math.max(appAssets.length, printAssets.length);

  // Build sorted page groups: pair appAssets[i] with printAssets[i]
  const pages = [];
  for (let i = 0; i < pageCount; i++) {
    const pageNo = i + 1;
    const appAsset = appAssets.find(a => parseInt((a.fileName || "").split("_")[0]) === pageNo) || appAssets[i] || null;
    const printAsset = printAssets.find(a => parseInt((a.fileName || "").split("_")[0]) === pageNo) || printAssets[i] || null;
    if (appAsset || printAsset) {
      pages.push({ pageIndex: i, pageNo, appAsset, printAsset });
    }
  }

  // getCollageOverlay — mirrors approval form's approach exactly:
  // sizeAlias extracted from filename (reliable), isRightPage always false for per-page preview
  // (the addition in layout methods is for the double-spread export canvas, not single-page display)
  const getCollageOverlay = (layoutNo, pageNo) => {
    if (!layoutNo) return null;
    const refAsset = appAssets[0] || printAssets[0] || null;
    const sizeAlias = getSizeAliasFromFileName(refAsset?.fileName) || "ml";
    const fnName = `${sizeAlias}_${String(layoutNo).replace(/\s+/g, "")}`;
    if (typeof LayoutMethods[fnName] !== "function") {
      console.warn("[AssetsView] LayoutMethods has no method:", fnName);
      return null;
    }
    const isFrontCover = pageNo === 1;
    const isBackCover = pageNo === pageCount && pageCount > 1;
    // Resolve sizeId from sizesList via the filename-derived sizeAlias — more reliable than
    // product.sizeId which may be absent when the product list API omits that field.
    const sizeItem = (sizesList || []).find(s => s.alias === sizeAlias);
    const resolvedSizeId = sizeItem?.id ?? product.sizeId;
    const { w: exportW, h: exportH } = getExportDims(selectedCategoryId, resolvedSizeId, isFrontCover, isBackCover);
    // isRightPage must always be false for per-page preview — passing true adds a full page width
    // to every slot's left coordinate (designed for double-spread export canvas), pushing slots off-canvas
    const collageInfoList = LayoutMethods[fnName]({ exportWidth: exportW, exportHeight: exportH, isRightPage: false });
    return { collageInfoList, exportWidth: exportW, exportHeight: exportH };
  };

  const handleSaveLayout = async (pageIndex, appAsset, printAsset) => {
    const newLayout = pageLayouts[pageIndex];
    if (!newLayout) return;

    setSavingLayoutPage(pageIndex);

    const calls = [appAsset, printAsset].filter(Boolean).map((asset) => {
      const formData = new FormData();
      formData.append("assetId", asset.id);
      formData.append("productId", product.productId);
      formData.append("layoutId", newLayout.layoutId);
      return HttpRequest(
        "POST",
        `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsAssetsUpdateLayout`,
        formData,
        "authorize"
      );
    });

    const results = await Promise.all(calls);
    const allSuccess = results.every((r) => r.isSuccess);

    if (allSuccess) {
      // Rename the layoutNo part (index 7) in filenames in state — no file upload
      const renameAsset = (asset) => {
        if (!asset) return asset;
        const parts = (asset.fileName || "").replace(/\.[^.]+$/, "").split("_");
        const ext = (asset.fileName || "").split(".").pop();
        if (parts.length >= 8) parts[7] = String(newLayout.layoutNo);
        return { ...asset, fileName: `${parts.join("_")}.${ext}`, layoutNo: newLayout.layoutNo };
      };

      const updatedGroups = (product.assetGroups || []).map((group) => ({
        ...group,
        assets: (group.assets || []).map((a) => {
          if (appAsset && a.id === appAsset.id) return renameAsset(a);
          if (printAsset && a.id === printAsset.id) return renameAsset(a);
          return a;
        }),
      }));
      onProductFieldChange({ assetGroups: updatedGroups });
      onSuccess("Layout updated successfully");

      setPageLayouts((prev) => {
        const updated = { ...prev };
        delete updated[pageIndex];
        return updated;
      });

    } else {
      const failed = results.find((r) => !r.isSuccess);
      onValidationError(failed?.message || "Failed to update layout.");
    }

    setSavingLayoutPage(null);
  };

  const handleImageUpload = (file, setter) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setter({ file, preview, dimensions: { width: img.width, height: img.height } });
    };
    img.src = preview;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ style: { borderRadius: 12, maxHeight: "95vh" } }}
    >
      <DialogTitle style={{ paddingBottom: 8 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" style={{ fontWeight: 700, color: "#333" }}>
            {product.label} — Assets
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers style={{ padding: 20 }}>
        <Box mb={2}>
          <Typography variant="body1" color="textSecondary">
            Size: {product.size} | Product ID: {product.productId}{product.totalPages > 0 ? ` | Total Pages: ${product.totalPages}` : ""}
          </Typography>
        </Box>


        {/* ── Rejected banner ── */}
        {isRejected && (
          <Box mb={2} p={1.5} style={{ backgroundColor: "#fff3e0", border: "1.5px solid #e65100", borderRadius: 6, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <Typography variant="body2" style={{ color: "#e65100", fontWeight: 600 }}>
              This theme has been <strong>Rejected</strong>. Status and Trending cannot be changed until the theme is re-approved. You may still edit the description and upload new assets.
            </Typography>
          </Box>
        )}

        {/* ── Active / Trending Radio Buttons ── */}
        <Box mb={2} display="flex" alignItems="center" style={{ gap: 32 }}>

          {/* ── Active / Inactive ── */}
          <Box style={{ opacity: isRejected ? 0.45 : 1, pointerEvents: isRejected ? "none" : "auto" }}>
            <Typography variant="caption" style={{ fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
              Status {isRejected && <span style={{ color: "#e65100", fontSize: 10 }}>(locked — rejected)</span>}
            </Typography>
            <RadioGroup
              row
              value={String(isActive)}
              onChange={async (e) => {
                const newValue = parseInt(e.target.value);
                const formData = new FormData();
                formData.append("productId", product.productId);
                formData.append("actionTypeId", newValue);
                const res = await HttpRequest(
                  "POST",
                  `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsStatusUpdate`,
                  formData,
                  "authorize"
                );
                if (res.isSuccess) {
                  setIsActive(newValue);
                  onProductFieldChange({ isActive: newValue });
                } else {
                  onValidationError("Failed to update status.");
                }
              }}
            >
              <FormControlLabel
                value="1"
                control={<Radio size="small" style={{ color: "#4caf50" }} />}
                label={<Typography variant="body2">Active</Typography>}
              />
              <FormControlLabel
                value="0"
                control={<Radio size="small" style={{ color: "#f44336" }} />}
                label={<Typography variant="body2">Inactive</Typography>}
              />
            </RadioGroup>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* ── Trending / Not Trending ── */}
          <Box style={{ opacity: isRejected ? 0.45 : 1, pointerEvents: isRejected ? "none" : "auto" }}>
            <Typography variant="caption" style={{ fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
              Trending {isRejected && <span style={{ color: "#e65100", fontSize: 10 }}>(locked — rejected)</span>}
            </Typography>
            <RadioGroup
              row
              value={String(isTrending)}
              onChange={async (e) => {
                const newValue = parseInt(e.target.value);
                const formData = new FormData();
                formData.append("productId", product.productId);
                formData.append("actionTypeId", newValue);
                const res = await HttpRequest(
                  "POST",
                  `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsTrendingUpdate`,
                  formData,
                  "authorize"
                );
                if (res.isSuccess) {
                  setIsTrending(newValue);
                  onProductFieldChange({ isTrending: newValue });
                } else {
                  onValidationError("Failed to update trending status.");
                }
              }}
            >
              <FormControlLabel
                value="1"
                control={<Radio size="small" style={{ color: "#ff9800" }} />}
                label={<Typography variant="body2">Trending</Typography>}
              />
              <FormControlLabel
                value="0"
                control={<Radio size="small" style={{ color: "#9e9e9e" }} />}
                label={<Typography variant="body2">Not Trending</Typography>}
              />
            </RadioGroup>
          </Box>

        </Box>



        {/* ── Description ── */}
        <Box mb={2}>
          <TextField
            label="Description"
            variant="outlined"
            size="small"
            value={descriptionValue}
            onChange={(e) => setDescriptionValue(e.target.value)}
            multiline
            rows={3}
            style={{ width: "100%" }}
            inputProps={{ maxLength: 500 }}
            helperText={editingDescription ? `${descriptionValue.length} / 500` : ""}
            InputProps={{
              readOnly: !editingDescription,
              endAdornment: (
                <InputAdornment position="end" style={{ alignSelf: "flex-start", marginTop: 8 }}>
                  {editingDescription ? (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Tooltip title="Save">
                        <IconButton
                          size="small"
                          onClick={() => onDescriptionSave(descriptionValue, () => setEditingDescription(false))}
                          style={{ color: "#4caf50" }}
                          disabled={isSavingDescription}
                        >
                          {isSavingDescription ? <CircularProgress size={14} /> : <CheckIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDescriptionValue(product?.description || "");
                            setEditingDescription(false);
                          }}
                          style={{ color: "#f44336" }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Tooltip title="Edit Description">
                      <IconButton size="small" onClick={() => setEditingDescription(true)} style={{ color: "#1976d2" }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* ── Preview & Thumbnail Images ── */}
        <Box mb={2}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, color: "#555", marginBottom: 8 }}>
            {selectedCategoryId == 6 ? "Preview & Thumbnail Images" : "Preview Image"}
          </Typography>
          <Grid container spacing={2}>

            {/* ── Preview Image ── */}
            <Grid item xs={12} sm={selectedCategoryId == 6 ? 6 : 4}>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                Preview Image
              </Typography>
              <input
                type="file"
                accept="image/*"
                id="modal-preview-upload"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleImageUpload(file, setPreviewImage);
                  e.target.value = "";
                }}
              />
              <label htmlFor="modal-preview-upload" style={{ cursor: "pointer", display: "block" }}>
                <Tooltip title={previewImage ? "Click to replace" : "Click to upload"}>
                  <div
                    style={{
                      width: "100%",
                      height: 160,
                      backgroundColor: "#f5f5f5",
                      border: previewImage ? "1px solid #1976d2" : "2px dashed #ccc",
                      borderRadius: 8,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                  >
                    {previewImage ? (
                      <img
                        src={previewImage.preview}
                        alt="preview"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    ) : product?.previewFile ? (
                      <img
                        src={getImageUrl(product.path + "/" + product.previewFile)}
                        alt="preview"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <div style={{ textAlign: "center", color: "#bbb" }}>
                        <span style={{ fontSize: 32 }}>🖼️</span>
                        <Typography variant="caption" style={{ color: "#bbb", display: "block", marginTop: 4 }}>
                          Click to upload
                        </Typography>
                      </div>
                    )}
                  </div>
                </Tooltip>
              </label>

              {/* ── File info ── */}
              <Box mt={0.5} style={{ minHeight: 40 }}>
                {previewImage ? (
                  <>
                    <Typography variant="caption" color="textSecondary"
                      style={{ display: "block", wordBreak: "break-all" }}
                      title={previewImage.file.name}
                    >
                      📄 {previewImage.file.name}
                    </Typography>
                    {previewImage.dimensions && (
                      <Typography variant="caption" style={{ color: "#888", display: "block" }}>
                        📐 {previewImage.dimensions.width} × {previewImage.dimensions.height} px
                      </Typography>
                    )}
                    <Box display="flex" justifyContent="flex-end">
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => setPreviewImage(null)} style={{ color: "#f44336", padding: 4 }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </>
                ) : product?.previewFile ? (
                  <Typography variant="caption" color="textSecondary"
                    style={{ display: "block", wordBreak: "break-all" }}
                  >
                    📄 {product.previewFile}
                  </Typography>
                ) : null}
              </Box>
            </Grid>

            {/* ── Thumbnail Image — Calendar only ── */}
            {selectedCategoryId == 6 && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                  Thumbnail Image
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  id="modal-thumbnail-upload"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleImageUpload(file, setThumbnailImage);
                    e.target.value = "";
                  }}
                />
                <label htmlFor="modal-thumbnail-upload" style={{ cursor: "pointer", display: "block" }}>
                  <Tooltip title={thumbnailImage ? "Click to replace" : "Click to upload"}>
                    <div
                      style={{
                        width: "100%",
                        height: 160,
                        backgroundColor: "#f5f5f5",
                        border: thumbnailImage ? "1px solid #1976d2" : "2px dashed #ccc",
                        borderRadius: 8,
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "opacity 0.2s",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                    >
                      {thumbnailImage ? (
                        <img
                          src={thumbnailImage.preview}
                          alt="thumbnail"
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      ) : product?.thumbnailFile ? (
                        <img
                          src={getImageUrl(product.path + "/" + product.thumbnailFile)}
                          alt="thumbnail"
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      ) : (
                        <div style={{ textAlign: "center", color: "#bbb" }}>
                          <span style={{ fontSize: 32 }}>🖼️</span>
                          <Typography variant="caption" style={{ color: "#bbb", display: "block", marginTop: 4 }}>
                            Click to upload
                          </Typography>
                        </div>
                      )}
                    </div>
                  </Tooltip>
                </label>

                {/* ── File info ── */}
                <Box mt={0.5} style={{ minHeight: 40 }}>
                  {thumbnailImage ? (
                    <>
                      <Typography variant="caption" color="textSecondary"
                        style={{ display: "block", wordBreak: "break-all" }}
                        title={thumbnailImage.file.name}
                      >
                        📄 {thumbnailImage.file.name}
                      </Typography>
                      {thumbnailImage.dimensions && (
                        <Typography variant="caption" style={{ color: "#888", display: "block" }}>
                          📐 {thumbnailImage.dimensions.width} × {thumbnailImage.dimensions.height} px
                        </Typography>
                      )}
                      <Box display="flex" justifyContent="flex-end">
                        <Tooltip title="Remove">
                          <IconButton size="small" onClick={() => setThumbnailImage(null)} style={{ color: "#f44336", padding: 4 }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </>
                  ) : product?.thumbnailFile ? (
                    <Typography variant="caption" color="textSecondary"
                      style={{ display: "block", wordBreak: "break-all" }}
                    >
                      📄 {product.thumbnailFile}
                    </Typography>
                  ) : null}
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
        <Divider style={{ marginBottom: 12 }} />

        {isAssetsLoading ? (
          <Box textAlign="center" mt={5}>
            <CircularProgress />
          </Box>
        ) : pages.length > 0 ? (
          <>
            {/* Per-page grouped view */}
            {pages.map(({ pageIndex, pageNo, appAsset, printAsset }) => {
              const currentPageLayout = pageLayouts[pageIndex];
              const layoutNo = currentPageLayout?.layoutNo || appAsset?.layoutNo || printAsset?.layoutNo;
              const overlay = layoutNo ? getCollageOverlay(layoutNo, pageNo) : null;
              const layoutChanged = !!currentPageLayout;

              const renderAssetCol = (asset, colLabel, colColor) => {
                if (!asset) {
                  return (
                    <Card elevation={2} style={{ borderRadius: 8, width: "100%", display: "flex", flexDirection: "column" }}>
                      <div style={{ width: "100%", height: 160, backgroundColor: "#f5f5f5", border: "2px dashed #ccc", borderRadius: "8px 8px 0 0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography variant="caption" style={{ color: "#bbb" }}>No {colLabel}</Typography>
                      </div>
                      <CardContent style={{ padding: "8px 10px" }}>
                        <Typography variant="caption" style={{ fontWeight: 600, color: colColor, display: "block" }}>{colLabel}</Typography>
                      </CardContent>
                    </Card>
                  );
                }
                return (
                  <Card elevation={2} style={{ borderRadius: 8, width: "100%", display: "flex", flexDirection: "column" }}>
                    <input
                      type="file"
                      accept="image/*"
                      id={`upload-${asset.id}`}
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) { e.target.value = ""; return; }
                        const objectUrl = URL.createObjectURL(file);
                        const img = new Image();
                        img.onload = () => {
                          URL.revokeObjectURL(objectUrl);
                          const expectedRatio = getExpectedAspectRatio(product.size, asset.fileName, selectedCategoryId, product.totalPages);
                          if (expectedRatio) {
                            const actualRatio = img.width / img.height;
                            const diff = Math.abs(actualRatio - expectedRatio);
                            if (diff > ASPECT_RATIO_TOLERANCE) {
                              onValidationError(
                                `Wrong aspect ratio for ${asset.fileName}. ` +
                                `Expected ~${expectedRatio.toFixed(4)} (±${ASPECT_RATIO_TOLERANCE}), ` +
                                `got ${actualRatio.toFixed(4)} (${img.width}×${img.height}px).`
                              );
                              return;
                            }
                          }
                          // Rebuild the canonical filename so the server stores the file
                          // with the correct naming convention, matching F15ThemeProductsAssetsForm:
                          // {pageNo}_{ic|fl}_tm_{catAlias}_{sizeAlias}_{themeCatLabel}_{themeLabel}_{layoutNo}.{ext}
                          // themeCatLabel and themeLabel are parsed from the existing fileName
                          // (already correctly encoded when first uploaded).
                          const mimeToExt = { "image/png": "png", "image/jpeg": "jpg", "image/jpg": "jpg", "image/gif": "gif" };
                          const newExt = mimeToExt[file.type] || "png";
                          const nameParts = (asset.fileName || "").replace(/\.[^.]+$/, "").split("_");
                          let renamedFile = file;
                          if (nameParts.length >= 7) {
                            const newLayoutNo = pageLayouts[pageIndex]?.layoutNo || asset.layoutNo || nameParts[7] || "0";
                            nameParts[7] = newLayoutNo;
                            const newName = `${nameParts.slice(0, 8).join("_")}.${newExt}`;
                            renamedFile = new File([file], newName, { type: file.type });
                          }
                          onAssetUpload(asset, renamedFile);
                        };
                        img.src = objectUrl;
                        e.target.value = "";
                      }}
                    />
                    <label htmlFor={`upload-${asset.id}`} style={{ cursor: "pointer", display: "block" }}>
                      <Tooltip title="Click to replace image">
                        <div style={{ width: "100%", height: 160, backgroundColor: "#f5f5f5", border: "1px solid #e0e0e0", borderRadius: "8px 8px 0 0", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", transition: "opacity 0.2s" }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = "0.85"}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                        >
                          <LazyImage
                            src={`${getImageUrl(asset.fileUrl)}${product._uploadTs ? `&t=${product._uploadTs}` : ""}`}
                            alt={asset.fileName}
                            fixedHeight={160}
                            selectedCategoryId={selectedCategoryId}
                          />
                        </div>
                      </Tooltip>
                    </label>
                    <CardContent style={{ padding: "8px 10px" }}>
                      <Typography variant="caption" style={{ fontWeight: 600, color: colColor, display: "block", marginBottom: 2 }}>{colLabel}</Typography>
                      <Typography variant="caption" color="textSecondary"
                        style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 2 }}
                        title={asset.fileName}
                      >
                        {asset.fileName}
                      </Typography>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="caption" style={{ color: "#888", fontSize: 10 }}>Layout: {asset.layoutNo}</Typography>
                        {uploadingAssetId === asset.id
                          ? <CircularProgress size={14} />
                          : <label htmlFor={`upload-${asset.id}`} style={{ cursor: "pointer" }}>
                              <Tooltip title="Upload new image">
                                <IconButton size="small" component="span"><PublishIcon style={{ fontSize: 14 }} /></IconButton>
                              </Tooltip>
                            </label>
                        }
                      </Box>
                    </CardContent>
                  </Card>
                );
              };

              return (
                <Box key={pageIndex} mb={2}>
                  <Typography variant="subtitle2" style={{ fontWeight: 600, color: "#555", marginBottom: 8 }}>
                    Page {pageNo}
                  </Typography>

                  <Grid container spacing={2} alignItems="stretch">
                    {/* App Asset (ic) */}
                    <Grid item xs={12} sm={5} style={{ display: "flex" }}>
                      {renderAssetCol(appAsset, "App Asset", "#1976d2")}
                    </Grid>

                    {/* Export Asset (fl) */}
                    <Grid item xs={12} sm={5} style={{ display: "flex" }}>
                      {renderAssetCol(printAsset, "Print Asset", "#e65100")}
                    </Grid>

                    {/* Layout column */}
                    <Grid item xs={12} sm={2} style={{ display: "flex" }}>
                      <Card elevation={2} style={{ borderRadius: 8, width: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-start", padding: 10 }}>
                        <Typography variant="caption" style={{ fontWeight: 600, color: "#555", display: "block", marginBottom: 6 }}>Layout</Typography>

                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          onClick={() => setLayoutPickerPage(pageIndex)}
                          style={{ borderColor: "#1976d2", color: "#1976d2", textTransform: "none", fontSize: 11, marginBottom: 4 }}
                        >
                          {layoutChanged ? "Change Layout" : "Browse Layouts…"}
                        </Button>

                        {layoutChanged && (
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            disabled={savingLayoutPage === pageIndex}
                            onClick={() => handleSaveLayout(pageIndex, appAsset, printAsset)}
                            style={{ backgroundColor: savingLayoutPage === pageIndex ? "#bbb" : "#4caf50", color: "#fff", textTransform: "none", fontSize: 11, marginBottom: 4 }}
                          >
                            {savingLayoutPage === pageIndex
                              ? <CircularProgress size={12} style={{ color: "#fff" }} />
                              : "Save Layout"
                            }
                          </Button>
                        )}

                        <Typography variant="caption" style={{ color: "#888", display: "block", marginBottom: 4, fontSize: 10 }}>
                          Shared for App &amp; Print
                        </Typography>

                        <LayoutPreview
                          exportWidth={overlay?.exportWidth}
                          exportHeight={overlay?.exportHeight}
                          collageInfoList={overlay?.collageInfoList}
                        />

                        <LayoutPickerDialog
                          open={layoutPickerPage === pageIndex}
                          onClose={() => setLayoutPickerPage(null)}
                          layoutsList={layoutsList || []}
                          onSelect={(layout) => {
                            setPageLayouts(prev => ({ ...prev, [pageIndex]: { layoutNo: layout.layoutNo, layoutId: layout.layoutId } }));
                            setLayoutPickerPage(null);
                          }}
                          selectedLayoutNo={layoutNo}
                          getCollageOverlay={(lNo) => getCollageOverlay(lNo, pageNo)}
                        />
                      </Card>
                    </Grid>
                  </Grid>

                  <Divider style={{ marginTop: 12 }} />
                </Box>
              );
            })}

            {/* Re-submit for Approval button */}
            {isRejected && (
              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isResubmitting}
                  onClick={onResubmitApproval}
                  style={{ backgroundColor: isResubmitting ? "#bbb" : "#1976d2", color: "#fff", fontWeight: 700, minWidth: 220 }}
                >
                  {isResubmitting ? <CircularProgress size={18} style={{ color: "#fff", marginRight: 8 }} /> : null}
                  Re-submit for Approval
                </Button>
              </Box>
            )}
          </>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No assets found.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CATEGORIES = [
  { categoryId: 2, categoryLabel: "Themed Photobooks" },
  { categoryId: 6, categoryLabel: "Photo Calendars" },
  { categoryId: 9, categoryLabel: "Photo Mugs" },
];

const handleAddNewTheme = () => {
  window.location = "#/F15ThemeProductsAssetsForm";
};

const handleAddNewThemeWithFolder = () => {
  window.location = "#/F15ThemeProductsAssetsFormWithFolder";
};


const F15AllThemedAssetsView = () => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [categoriesList, setCategoriesList] = useState([]); // just for dropdown [{categoryId, categoryLabel, productCount}]
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedCategoryProducts, setSelectedCategoryProducts] = useState(null); // {categoryLabel, products[]}
  const [isOpenSnackbar, setIsOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [isLoginMenu, setIsLoginMenu] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAssetsLoading, setIsAssetsLoading] = useState(false);
  const [uploadingAssetId, setUploadingAssetId] = useState(null);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [layoutsList, setLayoutsList] = useState([]);
  const [sizesList, setSizesList] = useState([]);
  const [isResubmitting, setIsResubmitting] = useState(false);

  // On mount: fetch all categories for dropdown only
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch all categories (light — just for dropdown list)
  const fetchCategories = async () => {
    setIsLoading(true);
    const res = await HttpRequest(
      "GET",
      `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesView`,
      null,
      "authorizeJson"
    );

    if (res.isSuccess) {
      const data = res.data || [];
      // Build dropdown list from response
      const list = data.map((category) => ({
        categoryId: category.categoryId,
        categoryLabel: category.categoryLabel,
        productCount: category.products ? category.products.length : 0,
      }));
      setCategoriesList(list);
    } else {
      if (res.statusCode === 401) {
        setIsLoginMenu(true);
      } else {
        handleSnackbar(true, res.message || "Failed to load categories", "error");
      }
    }
    setIsLoading(false);
  };

  const handleAssetUpload = async (asset, file) => {
    setUploadingAssetId(asset.id);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("assetId", asset.id);
    formData.append("themeLayoutId", asset.themeLayoutId);
    formData.append("productId", selectedProduct.productId);

    const res = await HttpRequest(
      "POST",
      `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsAssetsUpdate`,
      formData,
      "authorize"
    );

    if (res.isSuccess) {
      handleSnackbar(true, "Asset updated successfully", "success");
      // Reload assets for current product
      const reloadRes = await HttpRequest(
        "GET",
        `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsAssetsView?productId=${selectedProduct.productId}`,
        null,
        "authorizeJson"
      );
      if (reloadRes.isSuccess) {
        const data = reloadRes.data && reloadRes.data.length > 0 ? reloadRes.data[0] : null;
        if (data) {
          setSelectedProduct((prev) => ({
            ...prev,
            recordId: data.recordId || prev.recordId,
            label: data.label || prev.label,
            userLabel: data.userLabel || prev.userLabel,
            description: data.description || prev.description,
            previewFile: data.previewFile || prev.previewFile,
            thumbnailFile: data.thumbnailFile || prev.thumbnailFile,
            path: data.path || prev.path,
            assetGroups: data.assetsTypes || [],
            _uploadTs: Date.now(),
          }));
        }
      }
    } else {
      handleSnackbar(true, res.message || "Upload failed", "error");
    }

    setUploadingAssetId(null);
  };
  const handleProductFieldChange = (fields) => {
    setSelectedProduct((prev) => ({ ...prev, ...fields }));

    // ── Update selectedCategoryProducts (for main page buttons) ──
    setSelectedCategoryProducts((prev) =>
      prev.map((themeItem) => ({
        ...themeItem,
        themeProducts: themeItem.themeProducts
          ? themeItem.themeProducts.map((p) =>
            p.productId === selectedProduct.productId
              ? { ...p, ...fields }
              : p
          )
          : [],
      }))
    );
  };

  const handleDescriptionSave = async (description, onSuccess) => {
    setIsSavingDescription(true);

    const formData = new FormData();
    formData.append("productId", selectedProduct.productId);
    formData.append("description", description);

    const res = await HttpRequest(
      "POST",
      `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsDescriptionUpdate`,
      formData,
      "authorize"
    );

    if (res.isSuccess) {
      handleSnackbar(true, "Description updated successfully", "success");
      // Reload the same product page — identical to handleAssetUpload reload
      const reloadRes = await HttpRequest(
        "GET",
        `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsAssetsView?productId=${selectedProduct.productId}`,
        null,
        "authorizeJson"
      );
      if (reloadRes.isSuccess) {
        const data = reloadRes.data && reloadRes.data.length > 0 ? reloadRes.data[0] : null;
        if (data) {
          setSelectedProduct((prev) => ({
            ...prev,
            recordId: data.recordId || prev.recordId,
            label: data.label || prev.label,
            userLabel: data.userLabel || prev.userLabel,
            description: data.description || prev.description,
            previewFile: data.previewFile || prev.previewFile,
            thumbnailFile: data.thumbnailFile || prev.thumbnailFile,
            path: data.path || prev.path,
            assetGroups: data.assetsTypes || [],
          }));
        }
      }
      if (onSuccess) onSuccess();
    } else {
      handleSnackbar(true, res.message || "Failed to update description", "error");
    }

    setIsSavingDescription(false);
  };

  // On dropdown change: call API for selected category
  const handleCategoryChange = async (e) => {
    const categoryId = e.target.value;
    setSelectedCategoryId(categoryId);
    setSelectedCategoryProducts(null);

    if (!categoryId) return;

    setIsProductsLoading(true);
    const res = await HttpRequest(
      "GET",
      `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesView?categoryId=${categoryId}`,
      null,
      "authorizeJson"
    );

    if (res.isSuccess) {
      const data = res.data || [];
      // Assuming API returns array — take first match or adjust based on your API response shape
      // const categoryData = Array.isArray(data) ? data[0] : data;
      setSelectedCategoryProducts(Array.isArray(data) ? data : [data]);

    } else {
      if (res.statusCode === 401) {
        setIsLoginMenu(true);
      } else {
        handleSnackbar(true, res.message || "Failed to load products", "error");
      }
    }
    setIsProductsLoading(false);
  };

  const handleSnackbar = (isOpen, message, severity) => {
    setIsOpenSnackbar(isOpen);
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
  };

  const handleResubmitApproval = async () => {
    setIsResubmitting(true);
    const data = new FormData();
    data.append("recordId", selectedProduct.approvalRecordId);
    data.append("approvalStatus", 0);
    const res = await HttpRequest(
      "POST",
      `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesApprovalUpdate`,
      data,
      "authorize"
    );
    if (res.isSuccess) {
      handleSnackbar(true, "Theme re-submitted for approval", "success");
      setIsModalOpen(false);
      handleCategoryChange({ target: { value: selectedCategoryId } });
    } else {
      handleSnackbar(true, res.message || "Re-submit failed", "error");
    }
    setIsResubmitting(false);
  };

  const handleOpenModal = async (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
    setIsAssetsLoading(true);

    // Fetch assets, sizes, and layouts in parallel
    // Use selectedCategoryId (already set from dropdown) — product.categoryId is not
    // returned by the theme list API and would be undefined.
    const [res, sizesRes, layoutsRes] = await Promise.all([
      HttpRequest(
        "GET",
        `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsAssetsView?productId=${product.productId}`,
        null,
        "authorizeJson"
      ),
      HttpRequest(
        "GET",
        `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15CommonProductsSizesView?categoryId=${selectedCategoryId}`,
        null,
        "authorizeJson"
      ),
      HttpRequest(
        "GET",
        `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15CommonProductsThemeAssetsLayoutsController?categoryId=${selectedCategoryId}`,
        null,
        "authorizeJson"
      ),
    ]);

    if (sizesRes.isSuccess) setSizesList(sizesRes.data || []);
    if (layoutsRes.isSuccess) setLayoutsList(layoutsRes.data || []);

    if (res.isSuccess) {
      const data = res.data && res.data.length > 0 ? res.data[0] : null;
      if (data) {
setSelectedProduct({
          ...product,
          // ── new fields from updated API ──
          recordId: data.recordId || product.recordId,
          label: data.label || product.label,
          userLabel: data.userLabel || product.userLabel,
          description: data.description || "",
          previewFile: data.previewFile || "",
          thumbnailFile: data.thumbnailFile || "",
          path: data.path || "",
          isActive: data.isActive != null ? data.isActive : "1",
          isTrending: data.isTrending != null ? data.isTrending : "0",
          // ── map assetsTypes → assetGroups ──
          assetGroups: data.assetsTypes || [],
        });
      }
    } else {
      if (res.statusCode === 401) {
        setIsLoginMenu(true);
      } else {
        handleSnackbar(true, res.message || "Failed to load assets", "error");
      }
    }
    setIsAssetsLoading(false);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const getImageUrl = (fileUrl) => {
    const url = `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/albums/C01ImageView?fileName=${fileUrl}`;
    console.log("Image URL:", url);
    return url;
  };

  if (isLoading) {
    return (
      <Fragment>
        <NavBar isAuthorize />
        <div className={classes.loadingContainer}>
          <CircularProgress size={50} />
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <LoginMenu
        reload={true}
        open={isLoginMenu}
        handleClose={() => setIsLoginMenu(false)}
      />
      <CustomizedSnackbar
        isOpen={isOpenSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        handleCloseSnackbar={() => handleSnackbar(false, "", "")}
        autoHideDuration={3000}
      />
      <NavBar isAuthorize />
      <AssetsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        product={selectedProduct}
        getImageUrl={getImageUrl}
        isAssetsLoading={isAssetsLoading}
        onAssetUpload={handleAssetUpload}
        uploadingAssetId={uploadingAssetId}
        classes={classes}
        selectedCategoryId={selectedCategoryId}
        onDescriptionSave={handleDescriptionSave}
        isSavingDescription={isSavingDescription}
        onProductFieldChange={handleProductFieldChange}
        onValidationError={(msg) => handleSnackbar(true, msg, "error")}
        onSuccess={(msg) => handleSnackbar(true, msg, "success")}
        layoutsList={layoutsList}
        sizesList={sizesList}
        onResubmitApproval={handleResubmitApproval}
        isResubmitting={isResubmitting}
      />

      <div className={classes.root}>

        {/* ── DROPDOWN ── */}
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          mb={3}
          p={2}
          style={{ backgroundColor: "#f5f5f5", borderRadius: 8 }}
        >
          <Typography variant="subtitle1" style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
            Select Category:
          </Typography>
          <FormControl variant="outlined" size="small" style={{ minWidth: 250 }}>
            <Select
              value={selectedCategoryId}
              onChange={handleCategoryChange}
              displayEmpty
            >
              <MenuItem value="">
                <em>-- Select Category --</em>
              </MenuItem>
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat.categoryId} value={cat.categoryId}>
                  {cat.categoryLabel} &nbsp;
                  <span style={{ color: "#aaa", fontSize: "11px" }}>
                    (ID: {cat.categoryId})
                  </span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* ── Legend ── */}
          <Box display="flex" alignItems="center" style={{ gap: 12, marginLeft: 16 }}>
            {[
              { color: "#4caf50", label: "Active" },
              { color: "#f44336", label: "Inactive" },
              { color: "orange", label: "Trending" },
              { color: "#e65100", label: "Rejected" },
            ].map(({ color, label }) => (
              <Box key={label} display="flex" alignItems="center" style={{ gap: 5 }}>
                <div style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: color, flexShrink: 0 }} />
                <Typography variant="caption" style={{ color: "#555", fontWeight: 500, whiteSpace: "nowrap" }}>{label}</Typography>
              </Box>
            ))}
          </Box>

          <Box style={{ marginLeft: "auto", display: "flex", gap: "16px" }}>
            {/* <Button
              variant="contained"
              color="primary"
              onClick={() => handleAddNewThemeWithFolder()}
              style={{ marginLeft: "auto", backgroundColor: "#1976d2", color: "white" }}
            >
              + Add New Theme Via Folder
            </Button> */}

            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAddNewTheme()}
              style={{ marginLeft: "auto", backgroundColor: "#1976d2", color: "white" }}
            >
              + Add New Theme Manually
            </Button>
          </Box>



        </Box>

        {/* ── PRODUCTS LOADING ── */}
        {isProductsLoading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress size={36} />
          </Box>
        )}

        {/* ── PRODUCTS GRID ── */}
        {!isProductsLoading && selectedCategoryProducts && selectedCategoryProducts.length > 0 && (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
            {selectedCategoryProducts.map((themeItem) => (
              <Box key={themeItem.themeId}>
                {themeItem.themeProducts ? (
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "10px 12px",
                      backgroundColor: "#fff",
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      height: "100%",
                    }}
                  >
                    {/* Theme Header */}
                    <span style={{ fontWeight: 600, fontSize: "13px", color: "#333", display: "flex", alignItems: "center", gap: 6 }}>
                      {themeItem.themeId} : {themeItem.themeLabel}
                      <Tooltip title={`Add new theme under "${themeItem.themeLabel}"`}>
                        <button
                          onClick={() => {
                            const params = new URLSearchParams({
                              categoryId: selectedCategoryId,
                              themeLabel: themeItem.themeLabel,
                              themeCategoryId: themeItem.themeCategoryId,
                              description: themeItem.themeDescription || "",
                            });
                            window.location = `#/F15ThemeProductsAssetsForm?${params.toString()}`;
                          }}
                          style={{
                            width: 20, height: 20,
                            borderRadius: "50%",
                            border: "none",
                            backgroundColor: "#1976d2",
                            color: "#fff",
                            fontSize: 14,
                            fontWeight: 700,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            lineHeight: 1,
                            flexShrink: 0,
                          }}
                        >
                          +
                        </button>
                      </Tooltip>
                    </span>
                    <span style={{ fontSize: "11px", color: "#888" }}>
                      {themeItem.themeProducts.length} products
                    </span>

                    {/* Product Buttons */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                      {themeItem.themeProducts.map((product) => {
                        const isRejected = product.isApproved == 2;
                        return (
                          <button
                            key={product.productId}
                            title={isRejected ? "Rejected — only details can be edited" : ""}
                            style={{
                              padding: "3px 10px",
                              fontSize: "11px",
                              border: isRejected
                                ? "1.5px solid #e65100"
                                : `1px solid ${product.isActive == 0 ? "#f44336" : "#4caf50"}`,
                              borderRadius: "4px",
                              color: isRejected
                                ? "#e65100"
                                : product.isActive == 0
                                  ? product.isTrending == 0 ? "#f44336" : "white"
                                  : product.isTrending == 0 ? "#4caf50" : "white",
                              backgroundColor: isRejected
                                ? "#fff3e0"
                                : product.isTrending == 1 ? "orange" : "transparent",
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenModal(product);
                            }}
                          >
                            {(selectedCategoryId == 2 || selectedCategoryId == 6)
                              ? product.label.replace(/\s*-\s*Bundle of \d+/gi, "").trim()
                              : product.label
                            }{product.totalPages > 0 ? ` (${product.totalPages}pg)` : ""} Assets
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No products available for this theme.
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}


        {/* ── EMPTY STATE ── */}
        {!isProductsLoading && !selectedCategoryProducts && !isLoading && (
          <Box textAlign="center" mt={8}>
            <Typography variant="h6" color="textSecondary">
              👆 Select a category above to view products.
            </Typography>
          </Box>
        )}
      </div>
    </Fragment>
  );
};



export default F15AllThemedAssetsView;
